// Protected surface: review docs/protected-surfaces/app-shell-bootstrap.md before changing root entry, provider stack, or top-level route behavior.
// src/App.js

import React, { useState, Suspense, lazy, useEffect, memo, useRef } from 'react';
import { useLocation, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { UserProvider, useUser } from './contexts/userContext';
import { NotificationProvider } from './contexts/notificationContext';
import { ConfirmProvider } from './contexts/ConfirmContext';
import authApiClient from './authApiClient';
import ErrorBoundary from './ErrorBoundary';
import BrandStripe from './components/BrandStripe';
import ScrollToTop from './components/ScrollToTop';
import SkipToContent from './components/SkipToContent';
import { SnackbarProvider } from 'notistack';
import config from './config';
import PractitionerWorkspaceNav from './components/PractitionerWorkspaceNav.jsx';

// Import device detection utilities
import { markAsVisited, shouldRedirectInstalledAppToLogin } from './utils/deviceDetection';
import { initMobileOptimizations, isMobileDevice } from './utils/mobileUtils';
import { hasSocialMediaParams, getCleanPath } from './utils/urlUtils';
import { getMissionData } from './utils/missionDataHelper';
import { emitOutcomeEvent, getPageKeyFromPath, buildOutcomeTurnId } from './utils/outcomeEventClient';
import { readLastTurnIdForSession } from './utils/journeyTurnLinkStore';
import { getOrCreateTestRunId, isJourneyE2ETestModeEnabled, appendTestRunLog, getCurrentTestRunId } from './utils/testRunContext';
import { isAdminUser } from './utils/adminAccess';
import { GamificationProvider } from './contexts/GamificationContext';
import { buildApiUrl } from './utils/apiBase';
import {
  shouldEmitImplicitMissionOutcome,
  getImplicitMissionOutcomeEventType
} from './utils/missionOutcomeCoverage';

// Import logger initializer
import initializeLogger from './utils/loggerInitializer';
import logger from './utils/logger'; // <-- Import logger directly too

// Import your subscription function:
import { subscribeUserToPush } from './pushNotifications';

// Import your LoadingScreen component
import LoadingScreen from './LoadingScreen';

// Import Instagram Redirect Handler
import InstagramRedirectHandler from './components/InstagramRedirectHandler';

// Web application styles - order matters for CSS precedence
import './styles/global.css'; // Global styles first
import './styles/landingPages.css'; // Landing page styles second
import './styles/mobile.css'; // Mobile-specific styles
import './styles/mobile-touch.css'; // Enhanced touch interactions for mobile
import './styles/app-shell.css'; // App shell layout overrides (sidebar / appbar / dialog offsets)
import './AvatarCreationPage.css'; // Component-specific styles last

// Import the state management providers
import { GoalProvider } from './GoalStateManager';
import { AchievementsProvider } from './AchievementsStateManager';
import { RecommendationProvider } from './contexts/RecommendationContext';
import { MiniMeChatProvider } from './contexts/MiniMeChatContext';

// Protected & Admin routes
import ProtectedRoute from './ProtectedRoute';
import AdminRoute from './AdminRoute';
import ProfessionalRoute from './ProfessionalRoute';

// Import the useTimezoneUpdate hook
import useTimezoneUpdate from './hooks/useTimezoneUpdate';

// Single breakpoint constant for layout decisions in this file.
// Aligns with styles/app-shell.css and styles/mobile.css (768px boundary).
// Component-level layout should use the useIsMobile() hook instead.
const MOBILE_BREAKPOINT_PX = 768;

// PWA Entry Component - sends PWA users to /login, web users to landing
function PWAEntryOrLanding() {
  const isStandalone =
    window.navigator.standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches;

  if (isStandalone) {
    // LoginRouteGuard will instantly send authed users to /app
    return <Navigate to="/login" replace />;
  }

  // Web keeps the landing page
  return (
    <>
      <LandingPageTopNavigationBar />
      <LandingPageHome />
    </>
  );
}

const shouldTriggerDevErrorPage = () => {
  if (process.env.NODE_ENV === 'production' || typeof window === 'undefined') {
    return false;
  }

  const params = new URLSearchParams(window.location.search);
  return params.get('triggerErrorPage') === '1' || params.get('devErrorPage') === '1';
};

function DevErrorPageTrigger() {
  if (shouldTriggerDevErrorPage()) {
    throw new Error('Development ErrorPage trigger fired');
  }

  return null;
}

// PWA Mode Detection
const detectPWAMode = () => {
  const isStandalone = window.navigator.standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches;
  return isStandalone;
};

// Import components and pages (lazy-loaded)
const SideMenu = lazy(() => import('./SideMenu'));
const TopNavigationBar = lazy(() => import('./TopNavigationBar'));
const HomePage = lazy(() => import('./HomePage'));
const EvaluationPage = lazy(() => import('./EvaluationPage'));
const QuizPage = lazy(() => import('./QuizPage'));
const DashboardPage = lazy(() => import('./DashboardPage'));
const GoalPage = lazy(() => import('./GoalPage'));
const JournalPage = lazy(() => import('./JournalPage'));
const HumanAdvicePage = lazy(() => import('./HumanAdvicePage'));
const ChallengePage = lazy(() => import('./ChallengePage'));
const CommunityPage = lazy(() => import('./CommunityPage'));
const AccountPage = lazy(() => import('./AccountPage'));
const LoginPage = lazy(() => import('./LoginPage'));
const LoginRouteGuard = lazy(() => import('./components/LoginRouteGuard'));
const EarthPage = lazy(() => import('./EarthPage'));
const WaterPage = lazy(() => import('./WaterPage'));
const AirPage = lazy(() => import('./AirPage'));
const FirePage = lazy(() => import('./FirePage'));
const SupportPage = lazy(() => import('./SupportPage'));
const SignUpPage = lazy(() => import('./SignUpPage'));
const AvatarCreationPage = lazy(() => import('./AvatarCreationPage'));
const GiftPage = lazy(() => import('./GiftPage'));
const RecommendationPage = lazy(() => import('./RecommendationPage'));
const InsightsAndRemindersPage = lazy(() => import('./InsightsAndRemindersPage'));
const InsightsRemindersLanding = lazy(() => import('./InsightsRemindersLanding'));
const RemindersPage = lazy(() => import('./RemindersPage'));
const InsightsPage = lazy(() => import('./InsightsPage'));
const AdminHomePage = lazy(() => import('./AdminHomePage'));
const LandingPageTopNavigationBar = lazy(() => import('./LandingPageTopNavigationBar'));
const LandingPageHome = lazy(() => import('./LandingPageHome'));
const LandingPageContact = lazy(() => import('./LandingPageContact'));
const LandingPageAboutUs = lazy(() => import('./LandingPageAboutUs'));
const QuickAdvicePage = lazy(() => import('./QuickAdvicePage'));
const PassionChatSinglePage = lazy(() => import('./PassionChatSinglePage'));
const JourneyGuidePage = lazy(() => import('./JourneyGuidePage'));
const ChatsLanding = lazy(() => import('./ChatsLanding'));
const UnauthorizedPage = lazy(() => import('./UnauthorizedPage'));
const NotFoundPage = lazy(() => import('./NotFoundPage'));
const PricingPage = lazy(() => import('./PricingPage'));
const ReactivatePage = lazy(() => import('./ReactivatePage'));
const TestReactivationPage = lazy(() => import('./TestReactivationPage'));
const ReferralPage = lazy(() => import('./ReferralPage'));

const TrialExpiredPage = lazy(() => import('./TrialExpiredPage'));
const ClaritySessionLanding = lazy(() => import('./ClaritySessionLanding'));
const ClarityThankYou = lazy(() => import('./ClarityThankYou'));
const PrivacyPolicy = lazy(() => import('./PrivacyPolicy'));
const DeleteAccountPage = lazy(() => import('./DeleteAccountPage'));
const PaymentSuccessPage = lazy(() => import('./PaymentSuccessPage'));
const ForgotPasswordPage = lazy(() => import('./ForgotPasswordPage'));
const ResetPasswordPage = lazy(() => import('./ResetPasswordPage'));
const UpdateNotification = lazy(() => import('./utils/notifications/UpdateNotification'));
const GamificationBanner = lazy(() => import('./components/GamificationBanner'));
const MiniMeLevelUpCelebration = lazy(() => import('./components/MiniMeLevelUpCelebration'));
const TrialStatusBanner = lazy(() => import('./components/TrialStatusBanner')); // Added import for ProfessionalRoute

// Import the InspirationPage component
const InspirationPage = lazy(() => import('./InspirationPage'));
const PractitionerPage = lazy(() => import('./PractitionerPage'));
const PractitionerPatientWorkspacePage = lazy(() => import('./PractitionerPatientWorkspacePage'));
const PractitionerOfferingsPage = lazy(() => import('./PractitionerOfferingsPage'));
const FriendsPage = lazy(() => import('./FriendsPage'));
const PractitionerSpacePage = lazy(() => import('./PractitionerSpacePage'));
const PractitionerDashboardPage = lazy(() => import('./PractitionerDashboardPage'));

// LoginPageWrapper removed - was causing authentication loops
// Now using LoginPage directly with LandingPageTopNavigationBar

// --- Add module-level guard for SW registration ---
let swRegistered = false;

const getPractitionerWorkspaceActiveTab = (pathname = '') => {
  if (pathname === '/app/practitioner-dashboard') return 'dashboard';
  if (pathname === '/app/practitioners/offerings') return 'offerings';
  if (pathname === '/app/practitioners' || pathname.startsWith('/app/practitioners/patients/')) return 'patients';
  return '';
};

function PractitionerRouteFallback({ pathname }) {
  const active = getPractitionerWorkspaceActiveTab(pathname);

  return (
    <section className="practitioner-route-fallback" aria-live="polite">
      {active && <PractitionerWorkspaceNav active={active} />}
      <div className="practitioner-route-loading-card">
        <div className="loading-spinner"></div>
        <p>Loading this workspace...</p>
      </div>
    </section>
  );
}

/**
 * SUBCOMPONENT:
 * "ProtectedApp" is where we have access to the user from the context,
 * so we can call "subscribeUserToPush()" once the user is known.
 */
function ProtectedApp() {
  // Create unique instance identifier and render counter
  const instanceId = React.useRef(`protected-app-${Math.random().toString(36).substring(2, 9)}`);
  const renderCount = React.useRef(0);


  const [sideMenuOpen, setSideMenuOpen] = useState(false);

  // ResizeObserver error suppression lives in src/index.js (single point of truth).
  const location = useLocation();
  // Get the clean path without tracking parameters
  const cleanPath = getCleanPath();

  // App-shell layout overrides moved to styles/app-shell.css.

  // Drive desktop sidebar offset from React state to avoid first-load DOM races.
  useEffect(() => {
    const media = window.matchMedia(`(min-width: ${MOBILE_BREAKPOINT_PX + 1}px)`);
    const syncBodyClass = () => {
      const isDesktop = media.matches;
      document.body.classList.toggle('sidebar-open', Boolean(sideMenuOpen && isDesktop));
    };

    syncBodyClass();
    window.addEventListener('resize', syncBodyClass);
    media.addEventListener?.('change', syncBodyClass);

    return () => {
      window.removeEventListener('resize', syncBodyClass);
      media.removeEventListener?.('change', syncBodyClass);
    };
  }, [sideMenuOpen]);
  // Access the user context here
  const { user, isLoading } = useUser();
  const userIsAdmin = isAdminUser(user);


  // Use the timezone update hook to automatically set user's timezone
  useTimezoneUpdate();


  // Track side effects to ensure they only run once (handles StrictMode double mount)
  const sideEffectsDone = React.useRef(false);

  // Add component mount/unmount logging
  useEffect(() => {

    return () => {
    };
  }, []);

  // Right before returning JSX
  useEffect(() => {
  });

  // If URL has social media tracking parameters, redirect to clean path
  useEffect(() => {
    if (hasSocialMediaParams() && location.pathname !== cleanPath) {
      logger.info('[ProtectedApp] Detected social media params, redirecting to clean path:', cleanPath);
      window.history.replaceState({}, '', cleanPath);
    }
  }, [location.pathname, cleanPath]);

  // Do your page-visit tracking logic or anything else
  useEffect(() => {
    // Only track page visits on app routes, not public routes
    if (!location.pathname.startsWith('/app')) {
      return; // Don't track public routes
    }

    let startTime = Date.now();
    let currentPath = cleanPath; // Use clean path for tracking

    // Get or create session ID
    const getSessionId = () => {
      if (!window.pageTrackingSessionId) {
        window.pageTrackingSessionId = `session-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      }
      return window.pageTrackingSessionId;
    };

    return () => {
      const duration = Math.floor((Date.now() - startTime) / 1000); // Convert to seconds, floor for integer
      // Skip zero-duration visits (back-to-back navigations / accidental clicks)
      // unless this is a mission-attached page where dwell is part of the outcome signal.
      const missionAttached = !!(getMissionData(false)?.missionId || getMissionData(false)?.activeMissionId);
      if (duration <= 0 && !missionAttached) {
        return;
      }
      if (currentPath.startsWith('/app')) {
        // Track page visit with sessionId and use fetch with keepalive for reliability on unload
        const trackingData = {
          page: currentPath,
          duration,
          sessionId: getSessionId(),
          missionSessionId: getMissionData(false)?.missionId || getMissionData(false)?.activeMissionId || null,
          activeMissionId: getMissionData(false)?.activeMissionId || getMissionData(false)?.missionId || null,
          lastTurnId: (() => {
            const sessionCandidate = String(getMissionData(false)?.missionId || getMissionData(false)?.activeMissionId || '').trim() || null;
            return sessionCandidate ? (readLastTurnIdForSession(sessionCandidate) || null) : null;
          })(),
          ...(isJourneyE2ETestModeEnabled()
            ? { testRunId: getOrCreateTestRunId() }
            : {}),
          deviceInfo: {
            browser: navigator.userAgent,
            os: navigator.platform,
            device: navigator.vendor,
            screenResolution: `${window.screen.width}x${window.screen.height}`
          }
        };
        appendTestRunLog({
          event: 'page_visit_track',
          source: 'app_shell',
          page: currentPath,
          duration,
          missionSessionId: trackingData.missionSessionId || null
        }, trackingData.testRunId || getCurrentTestRunId());

        // Use fetch with keepalive if page is unloading (more reliable than sendBeacon for JSON)
        // Otherwise use regular POST
        if (document.visibilityState === 'hidden' && navigator.sendBeacon) {
          // Auth is via HttpOnly cookies (credentials:'include'); no need for a Bearer header.
          fetch(buildApiUrl('/track-page-visit'), {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              ...(trackingData.testRunId ? { 'x-test-run-id': trackingData.testRunId } : {})
            },
            body: JSON.stringify(trackingData),
            keepalive: true
          }).catch((error) => {
            logger.error('Error tracking page visit (unload):', error);
          });
        } else {
          authApiClient
            .post('/track-page-visit', trackingData)
            .catch((error) => {
              logger.error('Error tracking page visit:', error);
            });
        }

        // Part 1 implicit outcome: page dwell based fallback when explicit completion is missing.
        const missionData = getMissionData(false);
        const missionSessionId = String(missionData?.missionId || missionData?.activeMissionId || '').trim() || null;
        const pageKey = getPageKeyFromPath(currentPath);
        const shouldEmitImplicitOutcome = shouldEmitImplicitMissionOutcome({
          missionData,
          currentPath,
          pageKey
        });
        if (missionSessionId && shouldEmitImplicitOutcome) {
          const implicitTurnId = buildOutcomeTurnId();
          const lastTurnId = readLastTurnIdForSession(missionSessionId) || implicitTurnId;
          const durationMs = Math.max(0, duration * 1000);
          const eventType = getImplicitMissionOutcomeEventType(durationMs);
          const reasonCode = eventType === 'abandoned'
            ? (durationMs <= 10000 ? 'handoff_bounce' : 'read_no_action')
            : null;
          void emitOutcomeEvent({
            missionSessionId,
            turnId: implicitTurnId,
            lastTurnId,
            pageKey,
            eventType,
            route: currentPath,
            meta: {
              source: 'page_visit_timer',
              durationMs,
              reasonCode,
              handoffBounce: reasonCode === 'handoff_bounce',
              readWithoutAction: reasonCode === 'read_no_action'
            }
          }, {
            keepalive: document.visibilityState === 'hidden'
          });
        }
      }
    };
  }, [cleanPath, location.pathname]);

  // >>>>>> HERE: Actually subscribe to push after user is known <<<<<<
  useEffect(() => {
    // --- Add check for guard flag --- 
    if (
      swRegistered ||
      process.env.NODE_ENV !== 'production' ||
      !('serviceWorker' in navigator) ||
      isLoading || !user?.id
    ) {
      // Log why we are returning early
      if (swRegistered) logger.info('[ProtectedApp SW Effect] Skipping: Already registered.');
      if (process.env.NODE_ENV !== 'production') logger.info('[ProtectedApp SW Effect] Skipping: Not production.');
      if (!('serviceWorker' in navigator)) logger.info('[ProtectedApp SW Effect] Skipping: No serviceWorker support.');
      if (isLoading) logger.info('[ProtectedApp SW Effect] Skipping: User data still loading.');
      if (!user?.id) logger.info('[ProtectedApp SW Effect] Skipping: No user ID.');
      return;
    }
    // --- End check ---

    // --- Mark as started/done ---
    logger.info('[ProtectedApp SW Effect] Conditions met, attempting registration...');
    swRegistered = true;
    // --- End mark ---

    logger.info('[ProtectedApp] User is loaded, attempting SW registration and push subscription...');
    // Import the register function
    const serviceWorkerRegistration = require('./serviceWorkerRegistration');

    // Register the service worker first
    serviceWorkerRegistration.register()
      .then(() => {
        logger.info('[ProtectedApp] SW promise resolved – calling subscribeUserToPush()');
        // Add log right before the call (kept from previous step for debugging)
        logger.info('>>> [ProtectedApp] Calling subscribeUserToPush() now...');
        subscribeUserToPush().catch(err => logger.error('Error in subscribeUserToPush:', err));
      })
      .catch(err => {
        logger.error('Error during service worker registration:', err);
      });

  }, [user, isLoading]);

  // For toggling side menu
  const handleSideMenuToggle = () => {
    setSideMenuOpen(!sideMenuOpen);
    logger.info('Sidebar state changed:', !sideMenuOpen);
  };

  const wrapperClasses = `app-frame app-page ${(!userIsAdmin && sideMenuOpen) ? 'sidebar-open' : ''}`;
  const normalizedPathname = String(location.pathname || '').replace(/\/+$/, '') || '/';
  const isAppHomePath = normalizedPathname === '/app';
  const isAdminPath = normalizedPathname === '/app/admin' || normalizedPathname.startsWith('/app/admin/');
  const isPractitionerWorkspacePath =
    normalizedPathname === '/app/practitioner-dashboard'
    || normalizedPathname === '/app/practitioners'
    || normalizedPathname === '/app/practitioners/offerings'
    || normalizedPathname.startsWith('/app/practitioners/patients/');

  // Diagnostic: Check current state on location change
  useEffect(() => {
    // wait for layout to settle
    const t = setTimeout(() => {
      const sidebar = document.querySelector('.sidebar');
      const appbar = document.querySelector('.app-appbar, .MuiAppBar-root');

      if (sidebar && appbar) {
        const cssVar = getComputedStyle(document.documentElement).getPropertyValue('--sidebar-current').trim();
        const appbarLeft = getComputedStyle(appbar).left;
        const sidebarWidth = Math.round(sidebar.getBoundingClientRect().width);

        // Only log error if we are clearly initialized (width > 0) and mismatch persists
        if (sidebarWidth > 0 && appbarLeft !== cssVar && appbarLeft !== '0px') {
          // Suppress for now unless critical
          // console.warn('⚠️ [DIAGNOSTIC] Position mismatch detected:', { expected: cssVar, actual: appbarLeft });
        }
      }
    }, 1000);
    return () => clearTimeout(t);
  }, [location.pathname]);

  // Debug spacing for evaluation page on mobile
  useEffect(() => {
    if (typeof window === 'undefined' || !window?.matchMedia) return;
    if (location.pathname !== '/app/evaluation') return;

    // Diagnostic-only effect for evaluation-page spacing on phone-sized
    // viewports (600px = MUI sm). Not user-facing layout.
    const isMobile = window.matchMedia('(max-width: 600px)').matches;
    if (!isMobile) return;

    const checkSpacing = () => {
      const evalPage = document.querySelector('.evaluation-page');
      const evalReportSection = document.querySelector('.evaluation-page > .MuiBox-root:last-child');
      const appContainer = document.querySelector('.App-container');
      const mainEl = document.querySelector('main.App-content.main-content');

      if (!evalPage || !appContainer) return;

      const safeStyles = (el) => {
        if (!el || !(el instanceof Element)) return null;
        try { return window.getComputedStyle(el); } catch { return null; }
      };

      if (appContainer instanceof Element) {
        const s = safeStyles(appContainer);
        if (s) console.log('[Eval] APP-CONTAINER spacing:', {
          paddingBottom: s.paddingBottom,
          marginBottom: s.marginBottom
        });
      }

      if (mainEl instanceof Element) {
        const s = safeStyles(mainEl);
        if (s) console.log('[Eval] MAIN spacing:', {
          paddingBottom: s.paddingBottom,
          marginBottom: s.marginBottom
        });
      }

      if (evalPage instanceof Element) {
        const s = safeStyles(evalPage);
        if (s) console.log('[Eval] EVAL-PAGE spacing:', {
          paddingBottom: s.paddingBottom,
          marginBottom: s.marginBottom
        });
      }

      if (evalReportSection instanceof Element) {
        const s = safeStyles(evalReportSection);
        if (s) console.log('[Eval] REPORT-SECTION spacing:', {
          paddingBottom: s.paddingBottom,
          marginBottom: s.marginBottom
        });

        const lastPaper = evalReportSection.querySelector('.MuiPaper-root:last-child');
        if (lastPaper instanceof Element) {
          const ps = safeStyles(lastPaper);
          if (ps) console.log('[Eval] LAST-PAPER spacing:', {
            paddingBottom: ps.paddingBottom,
            marginBottom: ps.marginBottom
          });
        }

        const lastCaption = evalReportSection.querySelector('.MuiTypography-caption:last-child');
        if (lastCaption instanceof Element) {
          const cs = safeStyles(lastCaption);
          if (cs) console.log('[Eval] LAST-CAPTION spacing:', {
            paddingBottom: cs.paddingBottom,
            marginBottom: cs.marginBottom
          });
        }
      }

      if (evalPage instanceof Element) {
        try {
          const evalPageBottom = evalPage.getBoundingClientRect().bottom;
          const windowHeight = window.innerHeight;
          const extraSpace = windowHeight - evalPageBottom;
          console.log('[Eval] VIEWPORT:', {
            extraSpace: `${extraSpace}px`,
            hasExtraSpace: extraSpace > 10
          });
        } catch (e) {
          // Ignore getBoundingClientRect errors
        }
      }
    };

    const t = setTimeout(checkSpacing, 200);
    window.addEventListener('resize', checkSpacing);
    return () => {
      clearTimeout(t);
      window.removeEventListener('resize', checkSpacing);
    };
  }, [location.pathname]);

  // Render your side menu, top nav, and protected routes
  if (userIsAdmin && normalizedPathname.startsWith('/app') && !isAdminPath) {
    return <Navigate to="/app/admin" replace />;
  }

  return (
    <div className={wrapperClasses}>
      <BrandStripe />
      <GamificationProvider>
        <Suspense fallback={null}>
          <MiniMeLevelUpCelebration />
          <GamificationBanner />
        </Suspense>
        {!userIsAdmin && (
          <SideMenu
            open={sideMenuOpen}
            onMenuClose={handleSideMenuToggle}
            setMobileOpen={setSideMenuOpen}
          />
        )}
        {/* Only render TopNavigationBar on /app routes (non-admin app shell) */}
        {!userIsAdmin && location.pathname.startsWith('/app') && (
          <TopNavigationBar setMobileOpen={setSideMenuOpen} />
        )}
        <div className="App-container">
          <main id="main-content" className="App-content main-content">
            {!userIsAdmin && location.pathname.startsWith('/app') && (
              <Suspense fallback={null}>
                <TrialStatusBanner />
              </Suspense>
            )}
            <Suspense fallback={isPractitionerWorkspacePath ? <PractitionerRouteFallback pathname={normalizedPathname} /> : <LoadingScreen />}>
              {/* Render the appropriate page based on the current route */}
              {isAppHomePath && <HomePage />}
              {location.pathname === '/app/evaluation' && <EvaluationPage />}
              {location.pathname === '/app/quiz' && <QuizPage />}

              {location.pathname === '/app/dashboard' && <DashboardPage />}
              {/* Goals Page - Handles both list and specific goal details */}
              {location.pathname.startsWith('/app/goals') && <GoalPage />}
              {location.pathname === '/app/journal' && <JournalPage />}
              {location.pathname === '/app/advice' && <HumanAdvicePage />}
              {location.pathname === '/app/challenges' && <ChallengePage />}
              {location.pathname === '/app/friends' && <FriendsPage />}
              {location.pathname === '/app/community' && <CommunityPage />}
              {location.pathname === '/app/account' && <AccountPage />}
              {location.pathname === '/app/earth' && <EarthPage />}
              {location.pathname === '/app/water' && <WaterPage />}
              {location.pathname === '/app/air' && <AirPage />}
              {location.pathname === '/app/fire' && <FirePage />}
              {location.pathname === '/app/support' && <SupportPage />}
              {location.pathname === '/app/avatar' && <AvatarCreationPage />}
              {location.pathname === '/app/recommendations' && <RecommendationPage />}
              {location.pathname === '/app/insightsandreminders' && <InsightsRemindersLanding />}
              {location.pathname === '/app/insights' && <InsightsPage />}
              {location.pathname === '/app/reminders' && <RemindersPage />}
              {location.pathname === '/app/quickadvice' && <QuickAdvicePage />}
              {location.pathname === '/app/chats' && <ChatsLanding />}
              {location.pathname === '/app/passion-chat' && <PassionChatSinglePage />}
              {location.pathname === '/app/passion-chat-single' && <PassionChatSinglePage />}
              {location.pathname === '/app/journey-guide' && <JourneyGuidePage />}
              {location.pathname === '/app/gift' && <GiftPage />}
              {location.pathname === '/app/referral' && <ReferralPage />}
              {location.pathname === '/app/practitioners' && <PractitionerPage />}
              {location.pathname.startsWith('/app/practitioners/patients/') && <PractitionerPatientWorkspacePage />}
              {location.pathname === '/app/practitioners/offerings' && <PractitionerOfferingsPage />}
              {location.pathname === '/app/my-practitioners' && <Navigate to="/app/practitioner-space" replace />}
              {location.pathname === '/app/practitioner-space' && (
                <ProtectedRoute>
                  <ProfessionalRoute>
                    <PractitionerSpacePage />
                  </ProfessionalRoute>
                </ProtectedRoute>
              )}
              {location.pathname === '/app/practitioner-dashboard' && <PractitionerDashboardPage />}
              {location.pathname === '/app/inspiration' && <InspirationPage />}
              {location.pathname === '/app/admin' && <AdminRoute><AdminHomePage /></AdminRoute>}

              {/* Fallback for unknown app routes */}
              {location.pathname.startsWith('/app') && !location.pathname.match(/^\/app\/(evaluation|quiz|dashboard|goals|journal|advice|challenges|friends|community|account|earth|water|air|fire|support|avatar|recommendations|insightsandreminders|insights|reminders|quickadvice|chats|passion-chat|passion-chat-single|gift|referral|practitioners|my-practitioners|practitioner-space|practitioner-dashboard|inspiration|admin|journey-guide)(\/.*)?$/) && (
                (
                  location.state?.fromJourneyGuide
                  || location.state?.missionId
                  || location.state?.activeMissionId
                  || location.state?.missionRoute
                ) ? (
                  <Navigate
                    to="/app/journey-guide"
                    replace
                    state={{
                      ...(location.state || {}),
                      navFailure: {
                        reason: 'unknown_route',
                        blockedRoute: location.pathname,
                        blockedBy: 'AppRouteFallback',
                        missionSessionId: location.state?.missionId || location.state?.activeMissionId || null
                      },
                      missionReturnTs: Date.now()
                    }}
                  />
                ) : (
                  <Navigate to="/app" replace />
                )
              )}
            </Suspense>
          </main>
        </div>
      </GamificationProvider>
    </div>
  );
}

/**
 * The main App. Notice we still do your top-level routes and the
 * final "path='/*'" route now calls our "ProtectedApp" with providers.
 */
const App = memo(() => {
  const location = useLocation();
  const navigate = useNavigate();
  // Get the clean path without tracking parameters
  const cleanPath = getCleanPath();
  const prevPathRef = useRef(location.pathname);

  // Add unique identifier to track App renders
  const appInstanceId = React.useRef(`app-${Math.random().toString(36).substring(2, 9)}`);
  const appRenderCount = useRef(0);


  // Track navigation changes
  useEffect(() => {
    logger.info(`[App Navigation] Path changed to: ${location.pathname}`, {
      hasState: !!location.state,
      stateKeys: location.state ? Object.keys(location.state) : []
    });
    if (prevPathRef.current !== location.pathname) {
      prevPathRef.current = location.pathname;
    }
  }, [location.pathname, location.state]);

  logger.info(`[App ${appInstanceId.current}] App component rendering, path: ${location.pathname}`);

  // Initialize logger asynchronously on component mount (This logic runs only once)
  useEffect(() => {
    // Logger should be initialized before this effect runs, based on index.js logic
    logger.info('[App] Component did mount, logger should be ready.');
  }, []); // Empty dependency array ensures this runs only once on mount

  useEffect(() => {
    logger.info(`[App ${appInstanceId.current}] Main App component mounted or location changed, full URL:`, window.location.href);
    logger.info(`[App ${appInstanceId.current}] Route change detected - path: ${location.pathname}, clean path: ${cleanPath}`);

    // Initialize mobile optimizations
    initMobileOptimizations();

    // Detect PWA mode and log it
    const isPWAMode = detectPWAMode();
    if (isPWAMode) {
      logger.info('[App] Running in PWA mode - additional authentication checks may be needed');
      // Add PWA-specific class to body
      document.body.classList.add('pwa-mode');
    } else {
      document.body.classList.remove('pwa-mode');
    }

    // If we're on the landing page
    if (cleanPath === '/') {
      // Check if this is an initial launch of the installed PWA - if so, redirect to login
      if (shouldRedirectInstalledAppToLogin()) {
        logger.info('[App] Initial PWA launch detected, but waiting for userContext to check auth first...');
        // 🔧 CRITICAL FIX: Don't redirect immediately - let userContext check auth first
        // The redirect will happen in userContext if no valid session is found
      } else {
        // Otherwise, just mark as visited to prevent future redirects
        markAsVisited();
        logger.info('[App] Regular visit or navigation back to landing page, showing landing page');
      }
    }

    // Listen for the appInstalled event from the service worker
    const handleAppInstalled = (event) => {
      logger.info('[App] Received appInstalled event:', event.detail);
      // Only redirect if we're on the landing page
      if (cleanPath === '/') {
        logger.info('[App] Redirecting to login page after app installation');
        navigate('/login');
      }
    };

    window.addEventListener('appInstalled', handleAppInstalled);

    // Add mobile-specific class to body. Uses isMobileDevice() (user-agent +
    // viewport width) — different from useIsMobile, because this targets real
    // touch devices for behaviors like tap delays, not just narrow viewports.
    if (isMobileDevice()) {
      document.body.classList.add('mobile-view');
    } else {
      document.body.classList.remove('mobile-view');
    }

    // Cleanup function
    return () => {
      window.removeEventListener('appInstalled', handleAppInstalled);
    };
  }, [location.pathname, navigate, cleanPath]);

  return (
    <ErrorBoundary>
      <DevErrorPageTrigger />
      <SkipToContent />
      <ScrollToTop />
      <Suspense fallback={<LoadingScreen />}>
        <Suspense fallback={null}>
          <UpdateNotification />
        </Suspense>
        <UserProvider>
          <RecommendationProvider>
            <GoalProvider>
              <AchievementsProvider>
                <MiniMeChatProvider>
                  <NotificationProvider>
                    <SnackbarProvider maxSnack={3} autoHideDuration={3000}>
                      <ConfirmProvider>
                        <Routes>
                        {/* Public Routes */}
                        <Route
                          path="/"
                          element={<PWAEntryOrLanding />}
                        />
                        <Route
                          path="/clarity-session"
                          element={<ClaritySessionLanding />}
                        />
                        <Route
                          path="/clarity-confirmed"
                          element={<ClarityThankYou />}
                        />
                        <Route
                          path="/about"
                          element={
                            <>
                              <LandingPageTopNavigationBar />
                              <LandingPageAboutUs />
                            </>
                          }
                        />
                        <Route
                          path="/contact"
                          element={
                            <>
                              <LandingPageTopNavigationBar />
                              <LandingPageContact />
                            </>
                          }
                        />
                        <Route
                          path="/privacy"
                          element={
                            <>
                              <LandingPageTopNavigationBar />
                              <PrivacyPolicy />
                            </>
                          }
                        />
                        <Route
                          path="/privacyandroid"
                          element={<Navigate to="/privacy" replace />}
                        />
                        <Route
                          path="/delete-account"
                          element={
                            <>
                              <LandingPageTopNavigationBar />
                              <DeleteAccountPage />
                            </>
                          }
                        />
                        <Route path="/pricing" element={<PricingPage />} />
                        {/* Demo removed */}
                        <Route path="/trial-expired" element={<TrialExpiredPage />} />

                        <Route
                          path="/login"
                          element={
                            <LoginRouteGuard>
                              <>
                                <LandingPageTopNavigationBar />
                                <LoginPage />
                              </>
                            </LoginRouteGuard>
                          }
                        />
                        <Route
                          path="/signup"
                          element={
                            <>
                              <LandingPageTopNavigationBar />
                              <SignUpPage />
                            </>
                          }
                        />
                        <Route path="/unauthorized" element={<UnauthorizedPage />} />
                        <Route path="/payment-success" element={<PaymentSuccessPage />} />

                        {/* Forgot / Reset Password public routes */}
                        <Route path="/forgot-password" element={
                          <>
                            <LandingPageTopNavigationBar />
                            <ForgotPasswordPage />
                          </>
                        } />
                        <Route path="/reset-password" element={<ResetPasswordPage />} />

                        {/* Route for reactivation - NOT inside ProtectedRoute to avoid circular redirects */}
                        <Route path="/reactivate" element={
                          <>
                            <LandingPageTopNavigationBar />
                            <ReactivatePage />
                          </>
                        } />

                        {/* Route for testing reactivation - NOT inside ProtectedRoute to avoid circular redirects */}
                        <Route path="/test-reactivate" element={
                          <>
                            <LandingPageTopNavigationBar />
                            <TestReactivationPage />
                          </>
                        } />

                        {/* Inspiration route moved to protected app routes */}

                        {/* Test route for iOS Modal Force (bypasses device detection) */}


                        {/* Protected routes - ONLY for /app/* paths */}
                        <Route
                          path="/app/*"
                          element={
                            <ProtectedRoute>
                              {logger.info(`[App ${appInstanceId.current}] ProtectedRoute rendered - accessing existing providers`)}
                              <ProtectedApp />
                            </ProtectedRoute>
                          }
                        />

                        {/* Catch-all for unknown routes - render 404 page */}
                        <Route path="*" element={<NotFoundPage />} />
                        </Routes>
                      </ConfirmProvider>
                    </SnackbarProvider>
                  </NotificationProvider>
                </MiniMeChatProvider>
              </AchievementsProvider>
            </GoalProvider>
          </RecommendationProvider>
        </UserProvider>
      </Suspense>
    </ErrorBoundary>
  );
});

export default App;


/* Commented-Out Routes for Future Use or Reference
// <Route path="/app/reminders" element={<RemindersPage />} /> // Not in use for the first version. combined in Insights&Reminders
// <Route path="/app/insights" element={<InsightsPage />} /> // Not in use for the first version. combined in Insights&Reminders
// <Route path="/app/achievements" element={<AchievementsPage />} /> // Not in use for the first version, merged in dashboard
// <Route path="/app/progress" element={<ProgressPage />} /> // Not in use for the first version, merged in dashboard
// <Route path="/app/settings" element={<SettingsPage />} /> // Not in use for the first version*/

